import { useEffect, useRef, useState } from "react";

// A small self-contained dropdown (no Radix dropdown package in the app). Closes
// on outside-click, Escape, or after an item is chosen.
export default function WalletActionsMenu({
  onShowLedger,
  onUploadMore,
  onManageFiles,
  onRepromote,
  ledgerDisabled = false,
  repromoteDisabled = false,
}: {
  onShowLedger: () => void;
  onUploadMore: () => void;
  onManageFiles: () => void;
  onRepromote: () => void;
  ledgerDisabled?: boolean;
  repromoteDisabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };

  const item =
    "block w-full px-4 py-2 text-left text-sm hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-1 rounded bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800"
      >
        Actions
        <span aria-hidden className="text-xs">▾</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 w-52 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            className={item}
            disabled={ledgerDisabled}
            onClick={choose(onShowLedger)}
            title="Open the full, editable ledger for this wallet"
          >
            Show ledger…
          </button>
          <button type="button" role="menuitem" className={item} onClick={choose(onUploadMore)}>
            Upload more…
          </button>
          <button type="button" role="menuitem" className={item} onClick={choose(onManageFiles)}>
            Manage files…
          </button>
          <button
            type="button"
            role="menuitem"
            className={item}
            disabled={repromoteDisabled}
            onClick={choose(onRepromote)}
            title="Re-run the full promotion over every staged file"
          >
            Re-promote all
          </button>
        </div>
      )}
    </div>
  );
}
